import { useRef, useCallback } from 'react';

// ── Helper functions (module-level, not exported) ──────────────────────────

function dcThinking(body) {
  const th = document.createElement('div');
  th.className = 'thinking';
  th.innerHTML = '<span></span><span></span><span></span>';
  body.appendChild(th);
  body.scrollTop = body.scrollHeight;
  return th;
}

function dcUserMsg(body, text) {
  const u = document.createElement('div');
  u.className = 'u-msg';
  u.textContent = text;
  body.appendChild(u);
  body.scrollTop = body.scrollHeight;
}

function dcAiStart(body) {
  const a = document.createElement('div');
  a.className = 'a-msg';
  const header = document.createElement('div');
  header.style.cssText = 'display:flex;align-items:center;gap:7px;margin-bottom:8px';
  header.innerHTML = `
    <div style="width:22px;height:22px;border-radius:50%;background:#0A0E1A;display:flex;align-items:center;justify-content:center;flex-shrink:0">
      <span style="font-family:monospace;font-size:12px;font-weight:500;color:#fff;line-height:1">n</span>
    </div>
    <span style="font-size:12px;font-weight:600;color:#374151">Nebula</span>`;
  a.appendChild(header);
  const c = document.createElement('div');
  c.className = 'a-content';
  a.appendChild(c);
  body.appendChild(a);
  return c;
}

function dcStream(c, text, onDone) {
  const body = c.closest('.chat-body');
  const words = text.split(' ');
  let i = 0;
  const cur = document.createElement('span');
  cur.style.cssText = 'display:inline-block;width:2px;height:12px;background:#0A0E1A;margin-left:2px;vertical-align:middle;animation:pulse .8s ease-in-out infinite';
  c.appendChild(cur);
  const si = setInterval(() => {
    if (i < words.length) {
      cur.remove();
      c.insertAdjacentText('beforeend', (i > 0 ? ' ' : '') + words[i++]);
      c.appendChild(cur);
      if (body) body.scrollTop = body.scrollHeight;
    } else {
      clearInterval(si);
      cur.remove();
      if (onDone) onDone();
    }
  }, 52);
  return si;
}

function dcStreamHTML(el, html, onDone) {
  const body = el.closest('.chat-body');
  const tokens = [];
  const regex = /(<br\s*\/?>|<[^>]+>[\s\S]*?<\/[^>]+>)|([^\s<]+)/g;
  let match;
  while ((match = regex.exec(html)) !== null) {
    if (match[1]) tokens.push({ type: 'html', content: match[1] });
    else if (match[2]) tokens.push({ type: 'text', content: match[2] });
  }
  let i = 0;
  const cur = document.createElement('span');
  cur.style.cssText = 'display:inline-block;width:2px;height:12px;background:#0A0E1A;margin-left:2px;vertical-align:middle;animation:pulse .8s ease-in-out infinite';
  el.appendChild(cur);
  const si = setInterval(() => {
    if (i < tokens.length) {
      cur.remove();
      const token = tokens[i++];
      const sep = i > 1 ? ' ' : '';
      if (token.type === 'html') {
        if (sep) el.insertAdjacentText('beforeend', sep);
        const tmp = document.createElement('span');
        tmp.innerHTML = token.content;
        while (tmp.firstChild) el.appendChild(tmp.firstChild);
      } else {
        el.insertAdjacentText('beforeend', sep + token.content);
      }
      el.appendChild(cur);
      if (body) body.scrollTop = body.scrollHeight;
    } else {
      clearInterval(si);
      cur.remove();
      if (onDone) onDone();
    }
  }, 52);
  return si;
}

function dcFadeIn(c, el) {
  el.style.cssText += ';opacity:0;transform:translateY(8px);transition:opacity .35s ease,transform .35s ease';
  c.appendChild(el);
  const body = c.closest('.chat-body');
  requestAnimationFrame(() => requestAnimationFrame(() => {
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
    if (body) body.scrollTop = body.scrollHeight;
  }));
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useChatDemo() {
  const timerRef = useRef(null);

  const clearDemo = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const runDemoChat = useCallback((bodyEl) => {
    if (!bodyEl) return;
    bodyEl.innerHTML = '';

    // Show initial thinking dots immediately
    const initThinking = dcThinking(bodyEl);

    timerRef.current = setTimeout(() => {
      if (initThinking.parentNode) initThinking.parentNode.removeChild(initThinking);

      // === MSG 1: AI Alert ===
      const c1 = dcAiStart(bodyEl);
      dcStreamHTML(c1, '⚠️ Danh mục của bạn đang có rủi ro cao!<br>Mỹ vừa áp thuế <strong>46%</strong> lên hàng xuất khẩu Việt Nam, VN-Index giảm <strong>3.8%</strong> ngay trong phiên. Hiện tại <strong>38.4%</strong> tài sản của bạn đang trong nhóm chịu ảnh hưởng trực tiếp. Nếu không điều chỉnh ngay, danh mục có thể tiếp tục giảm.', () => {
        // === MSG 2: User ===
        setTimeout(() => {
          dcUserMsg(bodyEl, 'Vậy tôi nên làm gì bây giờ?');
          // === MSG 3: AI Solution ===
          setTimeout(() => {
            const th2 = dcThinking(bodyEl);
            setTimeout(() => {
              if (th2.parentNode) th2.parentNode.removeChild(th2);
              const c2 = dcAiStart(bodyEl);
              dcStream(c2, 'Tôi có 2 đề xuất để giảm thiểu rủi ro ngay lúc này:', () => {
                setTimeout(() => {
                  const p1 = document.createElement('p');
                  p1.style.cssText = 'font-size:13.5px;color:#1A1A1A;margin-top:10px;line-height:1.75';
                  c2.appendChild(p1);
                  dcStreamHTML(p1, '1. <strong>Kích hoạt Hedging VN30 Futures</strong> – giúp bảo vệ tài sản của bạn khỏi rủi ro thua lỗ dù thị trường tiếp tục lao dốc', () => {
                    setTimeout(() => {
                      const p2 = document.createElement('p');
                      p2.style.cssText = 'font-size:13.5px;color:#1A1A1A;margin-top:4px;line-height:1.75';
                      c2.appendChild(p2);
                      dcStreamHTML(p2, '2. <strong>Điều chỉnh danh mục</strong> – phân bổ lại sang tài sản ít bị ảnh hưởng, giảm mức thua lỗ tiềm năng xuống còn <strong style="color:#EF4444">-6.7%</strong>', () => {
                        setTimeout(() => {
                          // Action rows box
                          const rows = document.createElement('div');
                          rows.style.cssText = 'background:#F9FAFB;border:1px solid #F3F4F6;border-radius:10px;padding:9px 12px;margin-top:6px;display:flex;flex-direction:column;gap:8px';
                          rows.innerHTML = `
                            <div style="margin-bottom:2px">
                              <div style="display:flex;align-items:center;gap:7px">
                                <span style="font-size:13px;font-weight:700;color:#EF4444;width:14px;flex-shrink:0">↓</span>
                                <span style="flex:1"><span style="font-size:12px;color:#6B7280;font-weight:600">Giảm tỷ trọng </span><span style="font-size:12px;font-weight:600;color:#374151">Cổ phiếu</span></span>
                                <span style="font-size:12px;white-space:nowrap"><strong style="color:#EF4444">38.4%</strong> → <strong style="color:#059669">22.5%</strong></span>
                              </div>
                              <div style="margin-left:21px;font-size:11.5px;color:#6B7280;margin-top:2px">Thuế tăng → doanh thu xuất khẩu giảm → nhóm này có thể tiếp tục lao dốc</div>
                            </div>
                            <div style="margin-bottom:2px">
                              <div style="display:flex;align-items:center;gap:7px">
                                <span style="font-size:13px;font-weight:700;color:#059669;width:14px;flex-shrink:0">↑</span>
                                <span style="flex:1"><span style="font-size:12px;color:#6B7280;font-weight:600">Tăng tỷ trọng </span><span style="font-size:12px;font-weight:600;color:#374151">Vàng</span></span>
                                <span style="font-size:12px;white-space:nowrap"><strong style="color:#374151">8.5%</strong> → <strong style="color:#059669">19.2%</strong></span>
                              </div>
                              <div style="margin-left:21px;font-size:11.5px;color:#6B7280;margin-top:2px">Bất ổn thương mại đẩy dòng tiền vào vàng – tài sản trú ẩn trong khủng hoảng</div>
                            </div>
                            <div>
                              <div style="display:flex;align-items:center;gap:7px">
                                <span style="font-size:13px;font-weight:700;color:#059669;width:14px;flex-shrink:0">↑</span>
                                <span style="flex:1"><span style="font-size:12px;color:#6B7280;font-weight:600">Tăng tỷ trọng </span><span style="font-size:12px;font-weight:600;color:#374151">Trái phiếu</span></span>
                                <span style="font-size:12px;white-space:nowrap"><strong style="color:#374151">5.0%</strong> → <strong style="color:#059669">14.8%</strong></span>
                              </div>
                              <div style="margin-left:21px;font-size:11.5px;color:#6B7280;margin-top:2px">Sinh lời ổn định khi cổ phiếu biến động, giúp cân bằng rủi ro danh mục</div>
                            </div>`;
                          dcFadeIn(c2, rows);
                          setTimeout(() => {
                            const n2 = document.createElement('p');
                            n2.style.cssText = 'font-size:13.5px;color:#1A1A1A;margin-top:12px;line-height:1.75';
                            c2.appendChild(n2);
                            dcStream(n2, 'Bạn có muốn tôi thực hiện giúp bạn luôn không?', () => {});
                          }, 500);
                        }, 400);
                      });
                    }, 150);
                  });
                }, 300);
              });
            }, 700);
          }, 400);
        }, 80);
      });
    }, 0);
  }, []);

  return { runDemoChat, clearDemo };
}
